import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Spinner } from './Button';
import { Input, type InputProps } from './Field';
import { IconSearch } from './icons';

/**
 * Autocomplete: a text input that searches an async source and lets the user
 * pick one result (an address, a place). For a fixed list of options use
 * `Combobox`.
 *
 * - `value`/`onChange` own the typed text; `onSelect` fires with the chosen
 *   item and the caller decides what the text becomes. Callers that need a
 *   *structured* value (an address) should clear it in `onChange` and treat
 *   the field as incomplete until `onSelect` fires again.
 * - `load(query)` runs 250ms after the last keystroke once `minLength`
 *   characters are typed; stale responses are dropped. Only typing
 *   searches: the caller rewriting `value` (after a pick) does not.
 * - Keyboard: ↑/↓ move, Enter picks, Esc closes; outside click closes.
 * - ARIA: `role=combobox` + `aria-controls` listbox, `aria-activedescendant`.
 */
export interface AutocompleteProps<T>
  extends Omit<
    InputProps,
    'value' | 'onChange' | 'onSelect' | 'leading' | 'trailing'
  > {
  value: string;
  onChange: (text: string) => void;
  onSelect: (item: T) => void;
  load: (query: string) => Promise<T[]>;
  keyOf: (item: T) => string;
  /** Two lines: primary + optional secondary. */
  renderOption: (item: T) => {
    primary: React.ReactNode;
    secondary?: React.ReactNode;
  };
  minLength?: number;
  emptyText?: string;
  errorText?: string;
}

export function Autocomplete<T>({
  value,
  onChange,
  onSelect,
  load,
  keyOf,
  renderOption,
  minLength = 3,
  emptyText = 'No matches.',
  errorText = 'Search failed. Try again.',
  className,
  disabled,
  onFocus,
  onKeyDown,
  ...rest
}: AutocompleteProps<T>) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const [results, setResults] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);

  const search = (text: string) => {
    window.clearTimeout(timer.current);
    const id = ++requestId.current;
    const q = text.trim();
    if (q.length < minLength) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    setFailed(false);
    setOpen(true);
    timer.current = window.setTimeout(async () => {
      try {
        const found = await load(q);
        if (requestId.current !== id) return;
        setResults(found);
        setActive(0);
      } catch {
        if (requestId.current === id) setFailed(true);
      } finally {
        if (requestId.current === id) setSearching(false);
      }
    }, 250);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const pick = (item: T) => {
    window.clearTimeout(timer.current);
    requestId.current += 1;
    setOpen(false);
    setResults([]);
    setSearching(false);
    onSelect(item);
  };

  const activeItem = open ? results[active] : undefined;
  const optionId = (item: T) =>
    `${listId}-${keyOf(item).replace(/[^\w-]/g, '_')}`;

  return (
    <div ref={rootRef} className={clsx('relative', className)}>
      <Input
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeItem ? optionId(activeItem) : undefined}
        autoComplete="off"
        leading={searching ? <Spinner /> : <IconSearch size={14} />}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={(e) => {
          if (value.trim().length >= minLength && results.length > 0)
            setOpen(true);
          onFocus?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((i) => (results.length ? (i + 1) % results.length : 0));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) =>
              results.length ? (i - 1 + results.length) % results.length : 0,
            );
          } else if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          } else if (e.key === 'Enter' && open && results[active]) {
            e.preventDefault();
            pick(results[active]);
          }
          onKeyDown?.(e);
        }}
        {...rest}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="v2-fade-in absolute z-50 mt-1 w-full rounded-v2-lg border border-v2-line bg-v2-surface p-1 shadow-v2-float"
        >
          {searching && results.length === 0 && (
            <li className="px-2 py-2 text-v2-12 text-v2-fg-3">Searching…</li>
          )}
          {!searching && failed && (
            <li className="px-2 py-2 text-v2-12 text-v2-danger">{errorText}</li>
          )}
          {!searching && !failed && results.length === 0 && (
            <li className="px-2 py-2 text-v2-12 text-v2-fg-3">{emptyText}</li>
          )}
          {results.map((item, i) => {
            const { primary, secondary } = renderOption(item);
            return (
              <li key={keyOf(item)} role="presentation">
                <button
                  id={optionId(item)}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(item)}
                  className={clsx(
                    'flex w-full flex-col rounded-v2 px-2 py-1.5 text-left transition-colors',
                    i === active ? 'bg-v2-sunken' : 'hover:bg-v2-sunken/60',
                  )}
                >
                  <span className="text-v2-13 font-medium text-v2-fg">
                    {primary}
                  </span>
                  {secondary && (
                    <span className="text-v2-12 text-v2-fg-3">{secondary}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
